import { Message as MessageCodec } from "@brain/proto/generated/brain_msgs";
import {
  ALL_CAMERA_SETTINGS_TOPICS,
  BRAIN_TOPICS_HEARTBEAT,
  BRAIN_TOPICS_STATUS,
} from "../../config/mqtt_config";
import { APP_MESSAGE_TYPES } from "../../config/protocol_config";
import {
  decodeAckInfo,
  decodeAutomode,
  decodeCameraParams,
  decodeControlRequest,
  decodeControlStatus,
  decodeKeyValue,
  decodeMission,
  decodeRemoteControl,
  decodeVehicleGenericInfo,
  decodeVehicleState,
  decodeWaypoints,
} from "../../interface/mqtt_codec";
import {
  logFrontCameraSettingsDebug,
  logRemoteClientDebug,
  logRemoteClientEvent,
} from "./remoteClientLogger";

export interface BinaryDataChannelDecodeResult {
  message: Record<string, any>;
  envelopePayloadByteLength: number;
}

export function toUint8Array(data: unknown): Uint8Array | null {
  if (data instanceof Uint8Array) {
    return data;
  }

  if (data instanceof ArrayBuffer) {
    return new Uint8Array(data);
  }

  if (ArrayBuffer.isView(data)) {
    return new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
  }

  return null;
}

export function getBinaryDataType(data: unknown) {
  if (data instanceof ArrayBuffer) {
    return "ArrayBuffer";
  }

  return data?.constructor?.name || typeof data;
}

function getByteLength(data: unknown): number {
  const bytes = toUint8Array(data);
  if (bytes) {
    return bytes.byteLength;
  }

  if (data && typeof (data as { length?: unknown }).length === "number") {
    return Number((data as { length: number }).length);
  }

  return 0;
}

function normalizeTopic(topic: string): string {
  return topic.replace(/^\/+/, "").replace(/-/g, "_");
}

export async function readBinaryDataChannelBytes(
  data: unknown,
): Promise<Uint8Array | null> {
  const bytes = toUint8Array(data);
  if (bytes) {
    return bytes;
  }

  if (typeof Blob !== "undefined" && data instanceof Blob) {
    if (typeof data.arrayBuffer === "function") {
      return new Uint8Array(await data.arrayBuffer());
    }

    if (typeof FileReader !== "undefined") {
      return new Uint8Array(
        await new Promise<ArrayBuffer>((resolve, reject) => {
          const reader = new FileReader();
          reader.onerror = () => {
            reject(reader.error || new Error("Failed to read Blob payload"));
          };
          reader.onload = () => {
            resolve(reader.result as ArrayBuffer);
          };
          reader.readAsArrayBuffer(data);
        }),
      );
    }
  }

  return null;
}

export function decodeBinaryDataChannelMessage(
  bytes: Uint8Array,
): BinaryDataChannelDecodeResult | null {
  if (!bytes) {
    return null;
  }

  const envelope = MessageCodec.decode(bytes);
  const topic = String(envelope.topic || "");
  const base = {
    topic,
    typeName: envelope.typeName || "",
  };
  const envelopePayloadByteLength = getByteLength(envelope.payload);
  const normalizedTopic = normalizeTopic(topic);
  const isMediaSettingsTopic =
    normalizedTopic.startsWith("media/") &&
    normalizedTopic.endsWith("/settings");

  const withPayloadByteLength = (message: Record<string, any>) => ({
    message,
    envelopePayloadByteLength,
  });

  switch (topic) {
    case BRAIN_TOPICS_STATUS.STATE:
      return withPayloadByteLength({
        __MESSAGE__: APP_MESSAGE_TYPES.stream,
        ...base,
        data: decodeVehicleState(envelope.payload),
      });

    case BRAIN_TOPICS_STATUS.AUTOMODE:
      return withPayloadByteLength({
        __MESSAGE__: APP_MESSAGE_TYPES.adMode,
        ...base,
        data: decodeAutomode(envelope.payload),
      });

    case BRAIN_TOPICS_STATUS.CONTROL:
      return withPayloadByteLength({
        __MESSAGE__: APP_MESSAGE_TYPES.controlStatus,
        ...base,
        data: decodeControlStatus(envelope.payload),
      });

    case BRAIN_TOPICS_STATUS.REMOTE_CONTROL:
      return withPayloadByteLength({
        __MESSAGE__: APP_MESSAGE_TYPES.remoteControl,
        ...base,
        data: decodeRemoteControl(envelope.payload),
      });

    case BRAIN_TOPICS_STATUS.KV:
    case BRAIN_TOPICS_STATUS.KV_ARRAY:
    case BRAIN_TOPICS_STATUS.ROUTING_REQ:
    case BRAIN_TOPICS_HEARTBEAT.PING:
      return withPayloadByteLength({
        __MESSAGE__: APP_MESSAGE_TYPES.kv,
        ...base,
        data:
          topic === BRAIN_TOPICS_STATUS.KV_ARRAY
            ? decodeCameraParams(envelope.payload)
            : decodeKeyValue(envelope.payload),
      });

    case BRAIN_TOPICS_STATUS.CAMERA_PARAMS:
      return withPayloadByteLength({
        __MESSAGE__: APP_MESSAGE_TYPES.cameraParams,
        ...base,
        data: decodeCameraParams(envelope.payload),
      });

    case BRAIN_TOPICS_STATUS.VEHICLE_REQUEST:
      return withPayloadByteLength({
        __MESSAGE__: APP_MESSAGE_TYPES.vehicleRequest,
        ...base,
        data: decodeControlRequest(envelope.payload),
      });

    case BRAIN_TOPICS_STATUS.VEHICLE_ACK_INFO:
      return withPayloadByteLength({
        __MESSAGE__: APP_MESSAGE_TYPES.ackInfo,
        ...base,
        data: decodeAckInfo(envelope.payload),
      });

    case BRAIN_TOPICS_STATUS.VEHICLE_INFO:
      return withPayloadByteLength({
        __MESSAGE__: APP_MESSAGE_TYPES.vehicleInfo,
        ...base,
        data: decodeVehicleGenericInfo(envelope.payload),
      });

    case BRAIN_TOPICS_STATUS.WAYPOINTS:
      return withPayloadByteLength({
        __MESSAGE__: APP_MESSAGE_TYPES.waypoints,
        ...base,
        data: decodeWaypoints(envelope.payload),
      });

    case BRAIN_TOPICS_STATUS.MISSION:
      return withPayloadByteLength({
        __MESSAGE__: APP_MESSAGE_TYPES.mission,
        ...base,
        data: decodeMission(envelope.payload),
      });

    default:
      if (
        (ALL_CAMERA_SETTINGS_TOPICS as readonly string[]).includes(topic) ||
        (isMediaSettingsTopic &&
          envelope.typeName === "brain_msgs.proto.KeyValueArray")
      ) {
        return withPayloadByteLength({
          __MESSAGE__: APP_MESSAGE_TYPES.cameraParams,
          ...base,
          data: decodeCameraParams(envelope.payload),
        });
      }

      return withPayloadByteLength({
        __MESSAGE__: APP_MESSAGE_TYPES.protoEnvelope,
        ...base,
        data: {
          payloadSize: envelope.payload?.length ?? 0,
        },
      });
  }
}

type RemoteClientDataHandler = (message: Record<string, any>) => void;

export function createDataChannelMessageHandler(
  applyMessage: RemoteClientDataHandler,
) {
  const applyDecodedBinaryMessage = (
    decoded: BinaryDataChannelDecodeResult | null,
  ) => {
    if (!decoded) {
      return;
    }

    const message = decoded.message;
    logFrontCameraSettingsDebug(message, decoded.envelopePayloadByteLength);
    logRemoteClientDebug("Decoded binary data channel message", {
      __MESSAGE__: message.__MESSAGE__,
      topic: message.topic ?? null,
      typeName: message.typeName ?? null,
    });
    applyMessage(message);
  };

  const decodeAndApplyBinaryMessage = async (data: unknown) => {
    try {
      const bytes = await readBinaryDataChannelBytes(data);
      if (!bytes) {
        console.warn("[AdRemoteClient] Ignoring non-text data channel message", {
          dataType: getBinaryDataType(data),
        });
        return;
      }

      applyDecodedBinaryMessage(decodeBinaryDataChannelMessage(bytes));
    } catch (err) {
      console.warn("[AdRemoteClient] Failed to decode binary data channel message", {
        dataType: getBinaryDataType(data),
        reason: err instanceof Error ? err.message : String(err),
      });
    }
  };

  return (msg: any) => {
    logRemoteClientEvent("onMessageHandler", msg);
    if (typeof msg?.data === "string") {
      try {
        applyMessage(JSON.parse(msg.data));
      } catch (err) {
        console.warn("[AdRemoteClient] Ignoring malformed data channel message", {
          preview: String(msg.data).slice(0, 120),
          reason: err instanceof Error ? err.message : String(err),
        });
      }
      return;
    }

    const immediateBytes = toUint8Array(msg?.data);
    if (immediateBytes) {
      try {
        applyDecodedBinaryMessage(
          decodeBinaryDataChannelMessage(immediateBytes),
        );
      } catch (err) {
        console.warn("[AdRemoteClient] Failed to decode binary data channel message", {
          dataType: getBinaryDataType(msg?.data),
          reason: err instanceof Error ? err.message : String(err),
        });
      }
      return;
    }

    void decodeAndApplyBinaryMessage(msg?.data);
  };
}
